import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import BusinessCard from "@/components/BusinessCard";
import ProjectFolderCard, { PROJECT_FOLDER_COLORS, tint, relativeDate } from "@/components/ProjectFolderCard";
import ProjectDetailModal from "@/components/ProjectDetailModal";
import ChooseFromBoardPanel from "@/components/ChooseFromBoardPanel";
import { toast, Toaster } from "sonner";
import {
  Pin, Share2, Copy, X, Trash2, Pencil, AlertTriangle, Link2, Link2Off, Eye,
  Plus, ChevronDown, ChevronLeft, ChevronRight, Users, Archive, Check, MoreHorizontal, FolderOpen,
} from "lucide-react";

const LIMIT = 36;
const BOARD_PAGE_SIZE = 12;
const PIN_COLORS = ["#57C07E", "#3B9AE0", "#F2C14E", "#EE6C4D", "#A579D6"];
const VISIBLE_CHIPS = 4;
const MAX_CATEGORIES = 5;
const SHARED_PERMISSION_LABEL = { view: "View", comment: "Comment", contribute: "Collaborate" };
const BLANK_PROJECT = { name: "", color_id: "sage", categories: [], visibility: "private", zip: "", timing: "", description: "" };

export default function MyBoard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [board, setBoard] = useState(null);   // { limit, count, pins[] }
  const [active, setActive] = useState("__all__");
  const [share, setShare] = useState(null);   // { enabled, token, pro_user_ids }
  const [showShare, setShowShare] = useState(false);
  const [selectMode, setSelectMode] = useState(false);   // choosing cards to share
  const [selected, setSelected] = useState([]);
  const [renameOpen, setRenameOpen] = useState(null);   // current section name
  const [renameText, setRenameText] = useState("");
  const [myProjects, setMyProjects] = useState({ projects: [], active_limit: 7, closed_limit: 15, active_count: 0, closed_count: 0 });
  const [sharedProjects, setSharedProjects] = useState([]);
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProject, setNewProject] = useState(BLANK_PROJECT);
  const [catDraft, setCatDraft] = useState("");
  const [moreOptions, setMoreOptions] = useState(false);
  const [openProject, setOpenProject] = useState(null); // { id, action }
  const [chooseFor, setChooseFor] = useState(null);     // freshly created project (step 2)
  const [projectTab, setProjectTab] = useState("active"); // active | closed (= Filing Cabinet)
  const [moreChips, setMoreChips] = useState(false);
  const [boardPage, setBoardPage] = useState(0);

  const load = () => {
    api.get("/boards/me").then(r => setBoard(r.data));
    api.get("/boards/share/status").then(r => setShare(r.data));
    api.get("/projects/mine").then(r => setMyProjects(r.data)).catch(() => {});
    api.get("/projects/shared-with-me").then(r => setSharedProjects(r.data.projects || [])).catch(() => {});
  };

  useEffect(() => {
    if (user === null) return;
    if (!user) { navigate("/login"); return; }
    if (user.role !== "client") { navigate("/dashboard"); return; }
    load();
  }, [user, navigate]);

  const sections = useMemo(() => {
    if (!board?.pins) return [];
    const groups = {};
    for (const p of board.pins) (groups[p.section] ||= []).push(p);
    return Object.entries(groups).map(([name, pins]) => ({ name, pins }));
  }, [board]);

  const visiblePins = useMemo(() => {
    if (!board?.pins) return [];
    return active === "__all__" ? board.pins : board.pins.filter(p => p.section === active);
  }, [board, active]);

  useEffect(() => { setBoardPage(0); }, [active]);

  const totalBoardPages = Math.max(1, Math.ceil(visiblePins.length / BOARD_PAGE_SIZE));
  const safePage = Math.min(boardPage, totalBoardPages - 1);
  const pageStart = safePage * BOARD_PAGE_SIZE;
  const pagePins = visiblePins.slice(pageStart, pageStart + BOARD_PAGE_SIZE);
  const placeholderCount = safePage === 0 ? Math.max(0, BOARD_PAGE_SIZE - pagePins.length) : 0;

  if (!user || user.role !== "client") return null;

  const activeProjects = myProjects.projects.filter(p => p.status === "active");
  const closedProjects = myProjects.projects.filter(p => p.status === "closed");
  const inCabinet = projectTab === "closed";

  const remove = async (pro_user_id) => {
    try {
      await api.delete(`/boards/pin/${pro_user_id}`);
      toast.success("Removed from board");
      load();
    } catch { toast.error("Could not remove"); }
  };

  const move = async (pro_user_id, currentSection) => {
    const next = window.prompt("Move to which section?", currentSection);
    if (!next || next === currentSection) return;
    try {
      await api.patch(`/boards/pin/${pro_user_id}`, { section: next.slice(0, 40) });
      toast.success(`Moved to "${next}"`);
      load();
    } catch { toast.error("Could not move"); }
  };

  const renameSection = async () => {
    if (!renameText.trim() || !renameOpen) return;
    try {
      await api.patch("/boards/sections/rename", { old: renameOpen, new: renameText.trim() });
      toast.success("Section renamed");
      if (active === renameOpen) setActive(renameText.trim());
      setRenameOpen(null); setRenameText("");
      load();
    } catch { toast.error("Could not rename"); }
  };

  const toggleSelect = (id) =>
    setSelected(list => list.includes(id) ? list.filter(x => x !== id) : [...list, id]);

  const startSelecting = (preset = []) => {
    setSelected(preset);
    setSelectMode(true);
    setShowShare(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const shareSelected = async () => {
    if (!selected.length) return;
    try {
      const r = await api.post("/boards/share/enable", { pro_user_ids: selected });
      setShare({ enabled: true, token: r.data.token, pro_user_ids: r.data.pro_user_ids });
      setSelectMode(false);
      setShowShare(true);
      toast.success(`Sharing ${r.data.pro_user_ids.length} card${r.data.pro_user_ids.length === 1 ? "" : "s"}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create share link"); }
  };

  const disableShare = async () => {
    try {
      await api.post("/boards/share/disable");
      setShare({ enabled: false, token: null, pro_user_ids: [] });
      toast.success("Share link revoked");
    } catch { toast.error("Could not revoke"); }
  };
  const shareUrl = share?.token ? `${window.location.origin}/board/share/${share.token}` : "";
  const copyShare = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied to clipboard"); }
    catch { toast("Link: " + shareUrl); }
  };

  const addCategory = () => {
    const c = catDraft.trim().slice(0, 60);
    if (!c) return;
    if (newProject.categories.length >= MAX_CATEGORIES) { toast.error(`Up to ${MAX_CATEGORIES} categories`); return; }
    if (newProject.categories.some(x => x.toLowerCase() === c.toLowerCase())) { setCatDraft(""); return; }
    setNewProject({ ...newProject, categories: [...newProject.categories, c] });
    setCatDraft("");
  };
  const removeCategory = (c) => setNewProject({ ...newProject, categories: newProject.categories.filter(x => x !== c) });

  const closeNewProject = () => { setShowNewProject(false); setMoreOptions(false); setCatDraft(""); setNewProject(BLANK_PROJECT); };

  const createProject = async () => {
    if (!newProject.name.trim()) { toast.error("Name your project"); return; }
    try {
      const r = await api.post("/projects", newProject);
      closeNewProject();
      setProjectTab("active");
      toast.success("Project created");
      load();
      setChooseFor(r.data);   // → Step 2: Choose from My Board
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "object" ? detail.message : detail || "Could not create project");
    }
  };

  const chipCls = (on) =>
    `shrink-0 text-[13px] px-4 py-1.5 rounded-full font-semibold transition ${
      on ? "bg-[#245544] text-white" : "bg-white text-[#2D2926]/70 border border-black/[0.07] hover:bg-black/[0.03]"
    }`;

  return (
    <div className="bg-[#F5F7F5] min-h-screen">
      <Header/>
      <Toaster position="top-center"/>
      <div className="max-w-7xl mx-auto px-6 py-8">

        {/* Header row — title left, category filters right (per approved render) */}
        <div className="flex items-end justify-between gap-6 mb-5 flex-wrap">
          <div>
            <h1 className="text-4xl font-bold text-[#2D2926]" style={{fontFamily:'Outfit'}} data-testid="myboard-title">My Board</h1>
            <p className="text-[15px] text-[#2D2926]/55 mt-1">Your personal neighborhood corkboard.</p>
          </div>

          <div className="flex items-center gap-2 flex-wrap" data-testid="myboard-tabs">
            <button onClick={() => setActive("__all__")} className={chipCls(active === "__all__")} data-testid="tab-all">All</button>
            {sections.slice(0, VISIBLE_CHIPS).map(s => (
              <button key={s.name} onClick={() => setActive(s.name)} className={chipCls(active === s.name)} data-testid={`tab-${s.name.replace(/\s+/g,'-')}`}>
                {s.name}
              </button>
            ))}
            {sections.length > 0 && (
              <div className="relative">
                <button onClick={() => setMoreChips(m => !m)} className={chipCls(false) + " flex items-center gap-1"} data-testid="myboard-more-sections">
                  More <ChevronDown size={13} className={`transition ${moreChips ? "rotate-180" : ""}`}/>
                </button>
                {moreChips && (
                  <div className="absolute right-0 top-10 z-30 bg-white rounded-xl shadow-xl border border-black/10 py-1 w-60 text-sm" data-testid="myboard-more-sections-menu">
                    {sections.map(s => (
                      <div key={s.name} className="flex items-center">
                        <button onClick={() => { setActive(s.name); setMoreChips(false); }} className="flex-1 text-left px-3 py-2 hover:bg-black/5 truncate">
                          {s.name} <span className="text-[#2D2926]/40">({s.pins.length})</span>
                        </button>
                        <button onClick={() => { setRenameOpen(s.name); setRenameText(s.name); setMoreChips(false); }}
                          className="px-3 py-2 hover:bg-black/5 text-[#2D2926]/50" title="Rename section"
                          data-testid={`rename-section-${s.name.replace(/\s+/g,'-')}`}><Pencil size={12}/></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* summary + share */}
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <p className="text-xs text-[#2D2926]/50 flex items-center gap-1.5 flex-wrap" data-testid="myboard-summary-line">
            <span data-testid="myboard-count">{board?.count ?? 0} / {board?.limit ?? LIMIT} saved pros</span>
            <span className="text-[#2D2926]/25">·</span>
            <span data-testid="myboard-active-count">Active Projects {myProjects.active_count}/{myProjects.active_limit}</span>
            <span className="text-[#2D2926]/25">·</span>
            <span data-testid="myboard-cabinet-count">Filing Cabinet {myProjects.closed_count}/{myProjects.closed_limit}</span>
            <span className="text-[#2D2926]/25">·</span>
            <span data-testid="myboard-shared-count">Shared with Me {sharedProjects.length}</span>
          </p>
          <button
            onClick={() => share?.enabled ? setShowShare(true) : startSelecting([])}
            className="px-4 py-1.5 rounded-full bg-white border border-black/[0.07] hover:bg-black/[0.03] text-xs font-semibold flex items-center gap-2"
            data-testid="myboard-share-btn"
          >
            <Share2 size={13}/> {share?.enabled ? "Sharing on" : "Share board"}
          </button>
        </div>

        {/* Selection mode — choose exactly which cards go in the link */}
        {selectMode && (
          <div className="sticky top-16 z-30 mb-4 bg-white border border-[#2E6B57]/40 rounded-2xl p-4 shadow-sm" data-testid="share-select-banner">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[220px]">
                <p className="font-bold text-[15px] text-[#2D2926]" style={{ fontFamily: "Outfit" }}>Choose what you want to share</p>
                <p className="text-xs text-[#2D2926]/60 mt-0.5">My Board is private. Only the cards you select will be shared — and only as view-only business cards.</p>
              </div>
              <span className="text-xs font-semibold text-[#245544]" data-testid="share-selected-count">{selected.length} selected</span>
              <button onClick={() => { setSelectMode(false); setSelected([]); }} className="px-3 py-2 text-xs font-semibold text-[#2D2926]/60 hover:text-[#2D2926]" data-testid="share-select-cancel">Cancel</button>
              <button onClick={shareSelected} disabled={!selected.length}
                className="px-4 py-2 text-xs font-semibold rounded-full bg-[#245544] text-white disabled:opacity-40 flex items-center gap-1.5"
                data-testid="share-selected-btn">
                <Share2 size={13}/> Share selected
              </button>
            </div>
          </div>
        )}

        {/* Empty state */}
        {board && board.count === 0 && (
          <div className="bg-white rounded-2xl border border-dashed border-black/15 p-12 text-center mb-8" data-testid="myboard-empty">
            <Pin size={32} className="mx-auto text-[#C79B6B] mb-3"/>
            <h2 className="text-xl font-bold mb-1" style={{fontFamily:'Outfit'}}>Start building your neighborhood board</h2>
            <p className="text-sm text-[#2D2926]/60 mb-5">Pin pros you may want to contact again.</p>
            <button onClick={() => navigate("/search")} className="btn-lime px-5 py-2 text-sm" data-testid="myboard-browse-btn">
              Browse Pros
            </button>
          </div>
        )}

        {/* A. Pinned Pro corkboard — up to 12 cards per page, empties filled
            with playful "pin a pro" tiles, pageable side-to-side (max 36). */}
        {board && board.count > 0 && (
          <div className="cork-board relative rounded-2xl p-6 md:p-7 mb-12" data-testid="myboard-corkboard">
            {totalBoardPages > 1 && (
              <div className="lg:hidden relative z-10 flex items-center justify-center gap-3 mb-5" data-testid="board-pager-mobile">
                <button
                  onClick={() => setBoardPage(p => Math.max(0, p - 1))}
                  disabled={safePage === 0}
                  className="px-3 h-9 rounded-full bg-white/90 flex items-center gap-1 text-xs font-bold text-[#2D2926] shadow disabled:opacity-30 disabled:pointer-events-none"
                  data-testid="board-pager-mobile-prev"
                ><ChevronLeft size={15}/> Prev</button>
                <div className="flex items-center gap-2">
                  {Array.from({ length: totalBoardPages }).map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setBoardPage(i)}
                      className={`rounded-full transition ${i === safePage ? "w-6 h-2.5 bg-white" : "w-2.5 h-2.5 bg-white/50 hover:bg-white/80"}`}
                      aria-label={`Board page ${i + 1}`}
                      data-testid={`board-pager-mobile-dot-${i}`}
                    />
                  ))}
                </div>
                <button
                  onClick={() => setBoardPage(p => Math.min(totalBoardPages - 1, p + 1))}
                  disabled={safePage >= totalBoardPages - 1}
                  className="px-3 h-9 rounded-full bg-white/90 flex items-center gap-1 text-xs font-bold text-[#2D2926] shadow disabled:opacity-30 disabled:pointer-events-none"
                  data-testid="board-pager-mobile-next"
                >Next <ChevronRight size={15}/></button>
              </div>
            )}

            {safePage > 0 && (
              <button
                onClick={() => setBoardPage(p => Math.max(0, p - 1))}
                className="hidden lg:grid absolute left-2.5 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white/92 place-items-center shadow-[0_4px_14px_rgba(0,0,0,0.25)] hover:bg-white hover:scale-105 transition"
                aria-label="Previous page"
                data-testid="board-arrow-prev"
              ><ChevronLeft size={20} className="text-[#2D2926]"/></button>
            )}
            {safePage < totalBoardPages - 1 && (
              <button
                onClick={() => setBoardPage(p => Math.min(totalBoardPages - 1, p + 1))}
                className="hidden lg:grid absolute right-2.5 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white/92 place-items-center shadow-[0_4px_14px_rgba(0,0,0,0.25)] hover:bg-white hover:scale-105 transition"
                aria-label="Next page"
                data-testid="board-arrow-next"
              ><ChevronRight size={20} className="text-[#2D2926]"/></button>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 relative z-10">
              {pagePins.map((p, idx) => {
                const i = pageStart + idx;
                return (
                <div key={p.user_id} className="relative group" data-testid={`board-pin-${p.user_id}`}>
                  {selectMode && !p.inactive && (
                    <button
                      onClick={() => toggleSelect(p.user_id)}
                      className={`absolute inset-0 z-30 rounded-xl transition-all ${selected.includes(p.user_id) ? "ring-[3px] ring-[#2E6B57] bg-[#2E6B57]/10" : "ring-2 ring-white/40 hover:ring-white/80 bg-black/0"}`}
                      aria-pressed={selected.includes(p.user_id)}
                      data-testid={`share-select-${p.user_id}`}
                    >
                      <span className={`absolute top-2 left-2 w-6 h-6 rounded-full grid place-items-center border-2 ${selected.includes(p.user_id) ? "bg-[#245544] border-[#245544]" : "bg-white/85 border-white"}`}>
                        {selected.includes(p.user_id) && <Check size={14} className="text-white"/>}
                      </span>
                    </button>
                  )}
                  {p.inactive ? (
                    <InactiveCard pin={p} onRemove={() => remove(p.user_id)} onMove={() => move(p.user_id, p.section)}/>
                  ) : (
                    <>
                      <span
                        className="absolute -top-2.5 left-1/2 -translate-x-1/2 w-4 h-4 rounded-full z-10"
                        style={{
                          background: `radial-gradient(circle at 32% 28%, #ffffff 8%, ${PIN_COLORS[i % PIN_COLORS.length]} 52%, ${PIN_COLORS[i % PIN_COLORS.length]} 78%, rgba(0,0,0,0.35))`,
                          boxShadow: "inset -1px -2px 3px rgba(0,0,0,0.28), inset 2px 2px 2px rgba(255,255,255,0.7), 1px 3px 5px rgba(0,0,0,0.4)",
                        }}
                      />
                      <BusinessCard pro={p} index={i} variant="grid"/>
                      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition" data-testid={`board-actions-${p.user_id}`}>
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); move(p.user_id, p.section); }}
                          className="p-1.5 rounded-full bg-white border border-black/10 shadow-sm hover:bg-black/5"
                          title="Move section"
                          data-testid={`move-pin-${p.user_id}`}
                        ><Pencil size={12}/></button>
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); remove(p.user_id); }}
                          className="p-1.5 rounded-full bg-white border border-red-200 shadow-sm hover:bg-red-50 text-red-600"
                          title="Remove from board"
                          data-testid={`remove-pin-${p.user_id}`}
                        ><Trash2 size={12}/></button>
                      </div>
                      <p className="text-[10px] uppercase tracking-wider text-white/75 mt-2 text-center font-medium drop-shadow">{p.section}</p>
                    </>
                  )}
                </div>
                );
              })}
              {Array.from({ length: placeholderCount }).map((_, idx) => (
                <button
                  key={`ph-${idx}`}
                  onClick={() => navigate("/search")}
                  className="group rounded-xl border-2 border-dashed border-white/45 bg-white/10 hover:bg-white/20 hover:border-white/70 transition min-h-[168px] flex flex-col items-center justify-center text-center px-4"
                  data-testid="board-placeholder-tile"
                >
                  <span className="w-10 h-10 rounded-full bg-white/20 grid place-items-center mb-2.5 group-hover:scale-110 group-hover:bg-[#2E6B57] transition">
                    <Plus size={20} className="text-white"/>
                  </span>
                  <p className="text-[13px] font-bold text-white/95 leading-tight" style={{ fontFamily: "Outfit" }}>Pin someone great here</p>
                  <p className="text-[11px] text-white/70 mt-1">Tap to browse pros</p>
                </button>
              ))}
            </div>

            {totalBoardPages > 1 && (
              <div className="hidden lg:flex relative z-10 items-center justify-center gap-2 mt-7" data-testid="board-pager">
                {Array.from({ length: totalBoardPages }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setBoardPage(i)}
                    className={`rounded-full transition ${i === safePage ? "w-6 h-2.5 bg-white" : "w-2.5 h-2.5 bg-white/50 hover:bg-white/80"}`}
                    aria-label={`Board page ${i + 1}`}
                    data-testid={`board-pager-dot-${i}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* B. Project folders */}
        <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
          <div>
            <h2 className="text-[28px] font-bold text-[#2D2926]" style={{ fontFamily: "Outfit" }}>
              {inCabinet ? "Filing Cabinet" : "Project folders"}
            </h2>
            <p className="text-[15px] text-[#2D2926]/55 mt-0.5">
              {inCabinet ? "Your closed projects, kept for future reference." : "Organize pinned professionals by project."}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setProjectTab(inCabinet ? "active" : "closed")}
              className={`px-5 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2 transition border ${
                inCabinet ? "bg-[#2D2926] text-white border-transparent" : "bg-white border-black/[0.09] text-[#2D2926] hover:bg-black/[0.03]"
              }`}
              data-testid={inCabinet ? "project-tab-active-btn" : "project-tab-cabinet-btn"}
            >
              {inCabinet ? <><FolderOpen size={15}/> Active projects</> : <><Archive size={15}/> Filing Cabinet</>}
            </button>
            <button onClick={() => setShowNewProject(true)} className="btn-lime px-5 py-2.5 text-sm flex items-center gap-2" data-testid="myboard-new-project-btn">
              <Plus size={15}/> New project
            </button>
          </div>
        </div>

        {!inCabinet ? (
          activeProjects.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 mb-14 text-center border border-dashed border-black/15 text-sm text-[#2D2926]/50" data-testid="myboard-projects-empty">
              Group pinned pros into a project folder (e.g. "Kitchen Remodel") to track who you're considering.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-10 gap-y-8 mb-14" data-testid="myboard-projects-active-grid">
              {activeProjects.map(p => <ProjectFolderCard key={p.id} project={p} onOpen={(proj, action) => setOpenProject({ id: proj.id, action })} onChanged={load}/>)}
            </div>
          )
        ) : (
          <div className="mb-14">
            {myProjects.closed_count >= myProjects.closed_limit && (
              <div className="bg-[#F5F2EB] rounded-xl px-4 py-3 text-xs text-[#2D2926]/70 mb-5" data-testid="filing-cabinet-full-banner">
                Your Filing Cabinet is full. Additional filing cabinets are coming to LIMYA in the future.
                <button disabled className="ml-2 px-2.5 py-1 rounded-full bg-white border border-black/10 text-[#2D2926]/40 cursor-not-allowed" data-testid="filing-cabinet-buy-more-disabled">Additional filing cabinets — coming soon</button>
              </div>
            )}
            {myProjects.closed_count >= myProjects.closed_limit - 5 && myProjects.closed_count < myProjects.closed_limit && (
              <p className="text-xs text-[#2D2926]/50 mb-5" data-testid="filing-cabinet-near-full-note">Your Filing Cabinet holds up to {myProjects.closed_limit} completed projects.</p>
            )}
            {closedProjects.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 text-center border border-dashed border-black/15 text-sm text-[#2D2926]/50" data-testid="myboard-cabinet-empty">
                Completed projects you move here will show up for future reference.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-10 gap-y-8" data-testid="myboard-projects-closed-grid">
                {closedProjects.map(p => <ProjectFolderCard key={p.id} project={p} onOpen={(proj, action) => setOpenProject({ id: proj.id, action })} onChanged={load}/>)}
              </div>
            )}
          </div>
        )}

        {/* D. Shared with me */}
        <div className="border-t border-black/[0.07] pt-8 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 items-start">
          <div>
            <h2 className="text-[26px] font-bold text-[#2D2926]" style={{ fontFamily: "Outfit" }}>Shared with Me</h2>
            <p className="text-[15px] text-[#2D2926]/55 mt-0.5">Projects a neighbor has shared with you.</p>
          </div>
          {sharedProjects.length === 0 ? (
            <div className="bg-white rounded-2xl p-6 text-center border border-dashed border-black/15 text-sm text-[#2D2926]/50" data-testid="myboard-shared-empty">
              No shared projects yet.
              <span className="block text-[13px] text-[#2D2926]/40 mt-0.5">Projects someone shares with you will appear here.</span>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="myboard-shared-grid">
              {sharedProjects.map(p => {
                const base = PROJECT_FOLDER_COLORS[p.color_id] || PROJECT_FOLDER_COLORS.sage;
                return (
                  <button key={p.id} onClick={() => setOpenProject({ id: p.id })}
                    className="text-left rounded-2xl bg-white border border-black/[0.06] p-4 flex items-center gap-4 hover:shadow-md transition"
                    data-testid={`shared-project-${p.id}`}>
                    <span className="w-14 h-12 rounded-xl grid place-items-center shrink-0" style={{ background: tint(base, 0.55) }}>
                      <Users size={20} style={{ color: tint(base, -0.1) }}/>
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-[15px] truncate text-[#2D2926]" style={{ fontFamily: "Outfit" }}>{p.name}</p>
                      <p className="text-[13px] text-[#2D2926]/55 truncate flex items-center gap-1.5">
                        Shared by {p.owner_name}
                        <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-[#E9F0EC] text-[#245544] shrink-0" data-testid={`shared-permission-${p.id}`}>
                          {SHARED_PERMISSION_LABEL[p.permission] || "View"}
                        </span>
                      </p>
                      <p className="text-[12px] text-[#2D2926]/45 truncate">
                        {p.pro_count} professional{p.pro_count === 1 ? "" : "s"} · Updated {relativeDate(p.updated_at)}
                      </p>
                    </div>
                    <MoreHorizontal size={16} className="text-[#2D2926]/35 shrink-0"/>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* STEP 1 — Create a project */}
      {showNewProject && (
        <div className="fixed inset-0 bg-black/50 grid place-items-center z-50 p-4" onClick={closeNewProject}>
          <div className="bg-white rounded-2xl p-7 w-full max-w-md max-h-[88vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-testid="new-project-modal">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h3 className="text-[22px] font-bold text-[#2D2926]" style={{ fontFamily: "Outfit" }}>Create a project</h3>
                <p className="text-sm text-[#2D2926]/55 mt-0.5">Start a new folder to keep your professionals organized.</p>
              </div>
              <button onClick={closeNewProject} className="p-1.5 rounded-full hover:bg-black/5 shrink-0" data-testid="new-project-close"><X size={18}/></button>
            </div>

            <label className="text-[13px] font-semibold text-[#2D2926] mb-1.5 block">Project name</label>
            <input value={newProject.name} onChange={e => setNewProject({ ...newProject, name: e.target.value })}
              placeholder="e.g. Kitchen Refresh" className="w-full px-3.5 py-2.5 border border-black/[0.12] rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]/40 focus:border-[#2E6B57]" data-testid="new-project-name" autoFocus/>

            <div className="mt-5">
              <label className="text-[13px] font-semibold text-[#2D2926] mb-1.5 block">
                Categories <span className="font-normal text-[#2D2926]/45">(add up to {MAX_CATEGORIES})</span>
              </label>
              <div className="flex flex-wrap gap-2 mb-2" data-testid="new-project-categories">
                {newProject.categories.map(c => (
                  <span key={c} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E9F0EC] text-[#2D2926] text-[13px] font-medium" data-testid={`new-project-category-chip-${c}`}>
                    {c}
                    <button onClick={() => removeCategory(c)} className="text-[#2D2926]/45 hover:text-[#2D2926]" aria-label={`Remove ${c}`} data-testid={`new-project-category-remove-${c}`}><X size={13}/></button>
                  </span>
                ))}
              </div>
              <input
                value={catDraft}
                onChange={e => setCatDraft(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addCategory(); } }}
                onBlur={addCategory}
                disabled={newProject.categories.length >= MAX_CATEGORIES}
                placeholder={newProject.categories.length >= MAX_CATEGORIES ? `Maximum ${MAX_CATEGORIES} categories` : "Add a category…"}
                className="w-full px-3.5 py-2.5 bg-[#F7F6F2] border border-black/[0.08] rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]/40 disabled:opacity-50"
                data-testid="new-project-category-input"
              />
            </div>

            <div className="mt-5">
              <p className="text-[13px] font-semibold text-[#2D2926] mb-2.5">Folder color</p>
              <div className="grid grid-cols-6 gap-3" data-testid="new-project-color-grid">
                {Object.entries(PROJECT_FOLDER_COLORS).map(([id, hex]) => {
                  const on = newProject.color_id === id;
                  return (
                    <button key={id} onClick={() => setNewProject({ ...newProject, color_id: id })} title={id.replace(/_/g, " ")}
                      className={`w-9 h-9 rounded-full grid place-items-center transition ${on ? "ring-2 ring-offset-2 ring-[#245544]" : "hover:scale-105"}`}
                      style={{ background: hex }} data-testid={`new-project-color-${id}`} aria-label={`Color ${id.replace(/_/g, " ")}`} aria-pressed={on}>
                      {on && <Check size={16} className="text-white drop-shadow" strokeWidth={3}/>}
                    </button>
                  );
                })}
              </div>
            </div>

            <button onClick={() => setMoreOptions(m => !m)} className="text-[13px] text-[#245544] font-semibold mt-5 flex items-center gap-1" data-testid="new-project-more-options">
              More options <ChevronDown size={13} className={`transition ${moreOptions ? "rotate-180" : ""}`}/>
            </button>
            {moreOptions && (
              <div className="space-y-2 mt-3">
                <select value={newProject.visibility} onChange={e => setNewProject({ ...newProject, visibility: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-black/[0.12] text-sm bg-white" data-testid="new-project-visibility">
                  <option value="private">Private</option>
                  <option value="invite">Shared by invite</option>
                  <option value="connections">Visible to Connections</option>
                  <option value="public">Public on profile</option>
                </select>
                <input value={newProject.zip} onChange={e => setNewProject({ ...newProject, zip: e.target.value })} placeholder="ZIP / neighborhood (optional)" className="w-full px-3 py-2.5 border border-black/[0.12] rounded-xl text-sm" data-testid="new-project-zip"/>
                <input value={newProject.timing} onChange={e => setNewProject({ ...newProject, timing: e.target.value })} placeholder="Desired timing (optional)" className="w-full px-3 py-2.5 border border-black/[0.12] rounded-xl text-sm" data-testid="new-project-timing"/>
                <textarea value={newProject.description} onChange={e => setNewProject({ ...newProject, description: e.target.value })} placeholder="Short description (optional)" rows={2} className="w-full px-3 py-2.5 border border-black/[0.12] rounded-xl text-sm" data-testid="new-project-description"/>
              </div>
            )}

            <div className="flex gap-3 mt-6 pt-5 border-t border-black/[0.07]">
              <button onClick={closeNewProject} className="flex-1 px-4 py-2.5 rounded-xl border border-black/[0.12] text-sm font-semibold hover:bg-black/[0.03]" data-testid="new-project-cancel">Cancel</button>
              <button onClick={createProject} className="btn-lime flex-1 px-5 py-2.5 text-sm" data-testid="new-project-create">Create project</button>
            </div>
          </div>
        </div>
      )}

      {/* STEP 2 — Choose from My Board (auto-opens after creation) */}
      {chooseFor && (
        <ChooseFromBoardPanel
          projectId={chooseFor.id}
          project={chooseFor}
          existingProIds={[]}
          onClose={() => setChooseFor(null)}
          onAdded={load}
        />
      )}

      {/* Rename section modal */}
      {renameOpen && (
        <div className="fixed inset-0 bg-black/50 grid place-items-center z-50 p-4" onClick={() => setRenameOpen(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="rename-modal">
            <h3 className="text-lg font-bold mb-3" style={{fontFamily:'Outfit'}}>Rename section</h3>
            <input
              value={renameText}
              onChange={e => setRenameText(e.target.value.slice(0, 40))}
              className="w-full px-3 py-2 border border-black/10 rounded-lg outline-none focus:ring-2 focus:ring-[#2E6B57]"
              data-testid="rename-input"
              autoFocus
            />
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => setRenameOpen(null)} className="px-4 py-2 text-sm" data-testid="rename-cancel">Cancel</button>
              <button onClick={renameSection} className="btn-lime px-5 py-2 text-sm" data-testid="rename-save">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Share modal */}
      {showShare && (
        <div className="fixed inset-0 bg-black/50 grid place-items-center z-50 p-4" onClick={() => setShowShare(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="share-modal">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-lg font-bold flex items-center gap-2" style={{fontFamily:'Outfit'}}><Share2 size={18}/> Share this board</h3>
                <p className="text-xs text-[#2D2926]/60 mt-1">View-only link for family or friends.</p>
              </div>
              <button onClick={() => setShowShare(false)} className="p-1 rounded-full hover:bg-black/5"><X size={16}/></button>
            </div>

            {!share?.enabled ? (
              <>
                <p className="text-sm text-[#2D2926]/70 mb-4">
                  Your board is private. Pick the cards you want to share and we'll create a
                  view-only link — nothing else on your board is exposed.
                </p>
                <button onClick={() => startSelecting([])} className="btn-lime w-full py-2 text-sm flex items-center justify-center gap-2" data-testid="share-enable-btn">
                  <Check size={14}/> Choose cards to share
                </button>
              </>
            ) : (
              <>
                <p className="text-xs text-[#2D2926]/60 mb-2">Your view-only link — sharing {share.pro_user_ids?.length || 0} card{(share.pro_user_ids?.length || 0) === 1 ? "" : "s"}:</p>
                <div className="flex gap-2 mb-3">
                  <input readOnly value={shareUrl} className="flex-1 px-3 py-2 text-xs border border-black/10 rounded-lg bg-black/5 truncate" data-testid="share-url-input"/>
                  <button onClick={copyShare} className="px-3 py-2 rounded-lg bg-[#2D2926] text-white text-xs font-semibold flex items-center gap-1" data-testid="share-copy-btn">
                    <Copy size={12}/> Copy
                  </button>
                </div>
                <p className="text-[11px] text-[#2D2926]/50 mb-4 italic">
                  Only the cards you selected are visible — no notes, no folders, no requests.
                  Cards you add to your board later are not added to this link. Suspended pros are hidden automatically.
                </p>
                <div className="grid grid-cols-3 gap-2">
                  <button onClick={() => window.open(shareUrl, "_blank")} className="px-2 py-2 rounded-lg bg-white border border-black/10 text-xs font-semibold flex items-center justify-center gap-1" data-testid="share-preview-btn">
                    <Eye size={12}/> Preview
                  </button>
                  <button onClick={() => startSelecting(share.pro_user_ids || [])} className="px-2 py-2 rounded-lg bg-white border border-black/10 text-xs font-semibold flex items-center justify-center gap-1" data-testid="share-edit-cards-btn">
                    <Pencil size={12}/> Edit cards
                  </button>
                  <button onClick={disableShare} className="px-2 py-2 rounded-lg bg-red-50 text-red-700 text-xs font-semibold flex items-center justify-center gap-1" data-testid="share-disable-btn">
                    <Link2Off size={12}/> Revoke
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {openProject && (
        <ProjectDetailModal projectId={openProject.id} initialAction={openProject.action} onClose={() => setOpenProject(null)} onChanged={load}/>
      )}
    </div>
  );
}


function InactiveCard({ pin, onRemove, onMove }) {
  return (
    <div className="relative rounded-xl border border-black/10 bg-[#F2F4F2] p-5 opacity-70" data-testid={`inactive-card-${pin.user_id}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-12 h-12 rounded-full bg-black/10 grid place-items-center grayscale">
          {pin.avatar_url ? (
            <img src={pin.avatar_url} alt="" className="w-full h-full object-cover rounded-full grayscale"/>
          ) : (
            <span className="font-bold text-[#2D2926]/40">{pin.business_name?.[0] || "?"}</span>
          )}
        </div>
        <div className="min-w-0">
          <h3 className="font-bold text-sm truncate" style={{fontFamily:'Outfit'}}>{pin.business_name}</h3>
          <p className="text-[10px] text-[#2D2926]/40 uppercase tracking-wider">{pin.category}</p>
        </div>
      </div>
      <div className="flex items-start gap-1.5 text-[11px] text-amber-700 bg-amber-50 rounded-md px-2 py-1.5 mb-2">
        <AlertTriangle size={12} className="shrink-0 mt-px"/>
        <span>{pin.inactive_reason === "suspended" ? "Profile temporarily unavailable." : "This pro is no longer active on LIMYA."}</span>
      </div>
      <div className="flex gap-1.5">
        <button onClick={onMove} className="flex-1 text-[11px] px-2 py-1 rounded-md bg-white border border-black/10 hover:bg-black/5" data-testid={`inactive-move-${pin.user_id}`}>Move</button>
        <button onClick={onRemove} className="flex-1 text-[11px] px-2 py-1 rounded-md bg-red-50 text-red-700 border border-red-200" data-testid={`inactive-remove-${pin.user_id}`}>Remove</button>
      </div>
      <p className="text-[10px] uppercase tracking-wider text-[#2D2926]/40 mt-2 text-center">{pin.section}</p>
    </div>
  );
}
